import { useRef } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

/**
 * Drop-in replacement for `useLiveQuery` that keeps the **returned reference
 * stable** when the underlying data didn't actually change. Dexie normally
 * returns a fresh array on every subscription firing — even if the content
 * is identical to the previous result — which defeats downstream
 * `useMemo`/`React.memo` that depend on the result.
 *
 * This wrapper does a shallow comparison (length + per-element `Object.is`)
 * against the previous result. If equal, it returns the previously cached
 * reference instead of the new one. Consumers see a **stable identity** until
 * the data truly changes.
 *
 * @example
 * // Before: `months` memo re-runs on every task-write, even no-op writes.
 * const tasks = useLiveQuery(() => db.tasks.toArray(), [])
 * const months = useMemo(() => buildMonths(tasks), [tasks])
 *
 * // After: memo only re-runs when a task genuinely differs.
 * const tasks = useStableLiveQuery(() => db.tasks.toArray(), [])
 * const months = useMemo(() => buildMonths(tasks), [tasks])
 *
 * @remarks
 * - Same signature as `useLiveQuery`: returns `undefined` while the query
 *   is still pending on first mount.
 * - For non-array scalar results, use `useLiveQuery` directly — React's
 *   `Object.is` dedup already handles primitives.
 * - Shallow-equal only. Dexie creates fresh task objects per firing, so if
 *   a single field changed the element references differ and we correctly
 *   re-render. No deep-equal is attempted (would be too expensive for large
 *   lists).
 */
export function useStableLiveQuery<T>(
  querier: () => T | Promise<T>,
  deps: unknown[] = [],
): T | undefined {
  const raw = useLiveQuery(querier, deps)
  const ref = useRef<T | undefined>(raw)
  if (!shallowEqual(ref.current, raw)) {
    ref.current = raw
  }
  return ref.current
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  // Array fast-path — the common case (task lists, project lists, etc.)
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (!Object.is(a[i], b[i])) return false
    }
    return true
  }
  return false
}
